/**
 * Sending a receipt to the thermal printer at the counter.
 *
 * There is no way for a web page to talk to a USB printer directly, and asking
 * a hotel to install a local print agent to sell a plate of biryani is not a
 * reasonable ask. What every counter already has is the printer installed in
 * Windows, so this prints through the browser - into a hidden frame sized to
 * the roll, so the counter gets the printer dialog with the receipt already the
 * right shape and nothing else on the page comes with it.
 *
 * The receipt itself is fixed-width text laid out in `@kortex/shared`, where it
 * is tested. All that happens here is putting it on a page the width of the
 * roll, in a monospaced font whose characters are exactly as wide as the
 * printer's own, so the columns land where the layout intended.
 */
import { RECEIPT_COLUMNS, type PaperWidth } from '@kortex/shared';

const STORE_KEY = 'kortex.paperWidth';

/** The roll this counter has loaded. Kept per browser: it is a till setting. */
export function paperWidth(): PaperWidth {
  const saved = Number(localStorage.getItem(STORE_KEY));
  return saved === 58 || saved === 80 ? saved : 80;
}

export function setPaperWidth(width: PaperWidth) {
  localStorage.setItem(STORE_KEY, String(width));
}

/**
 * Courier New advances exactly 0.6em per character, so a font this many
 * millimetres tall puts `columns` characters inside the printable strip.
 *
 * Not all of it: a full line is laid out at 97% of the strip, because printable
 * widths vary by a millimetre or two between printers and a line that only just
 * fits on paper wraps on the machine next to it.
 */
const printableMm = (width: PaperWidth): number => (width === 58 ? 48 : 72);

function styleSheet(width: PaperWidth): string {
  const columns = RECEIPT_COLUMNS[width];
  const printable = printableMm(width);
  const fontMm = (printable * 0.97) / (columns * 0.6);
  return `
    @page { size: ${width}mm auto; margin: 0; }
    html, body { margin: 0; padding: 0; background: #fff; }
    body { padding: 3mm ${(width - printable) / 2}mm 6mm; }
    pre {
      margin: 0;
      font-family: 'Courier New', Courier, monospace;
      font-size: ${fontMm.toFixed(3)}mm;
      line-height: 1.35;
      white-space: pre;
      color: #000;
      -webkit-font-smoothing: none;
    }
  `;
}

const escapeHtml = (text: string) =>
  text.replace(/[&<>]/g, (c) => (c === '&' ? '&amp;' : c === '<' ? '&lt;' : '&gt;'));

/**
 * Hands `text` to the browser's print dialog on a page the width of the roll.
 *
 * A frame rather than a popup window: popup blockers do not stop it, and the
 * counter never sees a second tab open and close behind the dialog.
 */
export function printThermal(text: string, width: PaperWidth = paperWidth()): void {
  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(frame);

  const doc = frame.contentDocument;
  if (!doc || !frame.contentWindow) {
    frame.remove();
    throw new Error('This browser would not open a print frame');
  }

  doc.open();
  doc.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>Receipt</title>` +
      `<style>${styleSheet(width)}</style></head>` +
      `<body><pre>${escapeHtml(text)}</pre></body></html>`,
  );
  doc.close();

  const view = frame.contentWindow;
  const send = () => {
    view.focus();
    view.print();
    // Chrome resolves print() before the dialog closes; the frame has to
    // outlive it or the dialog prints a blank page.
    window.setTimeout(() => frame.remove(), 1000);
  };

  if (doc.readyState === 'complete') send();
  else frame.addEventListener('load', send, { once: true });
}
